import { FC, useEffect, useState } from 'react';
import { useOutletContext, useParams } from 'react-router-dom';

import { Stack, Box, useTheme } from '@mui/material';
import moment from 'moment';

import { MasterIndexApi } from '@/api';
import { FileGrid, IViewItem, ViewCard, ViewItem } from '@/components';
import { DATE_FORMAT } from '@/config';
import { LocalStorageItems } from '@/constants';
import {
  IMasterAddress,
  IMasterAddressPopulated,
  IMasterPoint,
} from '@/models/master.address';
import {
  getAddressLabelText,
  getAddressPrePlan,
  getNormalizedAddressText,
} from '@/services';
import { getFormFields } from '@/services/service.formfieldLoader';
import { useAgencyStore, useToastStore } from '@/store';
import { formatDistance, formatPersonName, generateUniqueId } from '@/utils';

import { MasterIndexDetailsContext } from '../../components/MasterIndexDetailsPage';
import { MasterAddressViewFields } from '../data';

interface MasterIndexAddressViewContentProps {
  hideHeader?: boolean;
}

const PrePlanMdf = getFormFields();
export const MasterIndexAddressViewContent: FC<
  MasterIndexAddressViewContentProps
> = () => {
  const outletContext = useOutletContext<
    MasterIndexDetailsContext | undefined
  >();
  const setCustomTitle = outletContext?.setCustomTitle;
  const setLastUpdatedAt = outletContext?.setLastUpdatedAt;
  const { currentAgency, fetchAgency } = useAgencyStore();

  const agencyId = localStorage.getItem(LocalStorageItems.CurrentAgencyId);
  useEffect(() => {
    if (agencyId && !currentAgency) {
      fetchAgency(agencyId);
    }
  }, [agencyId]);
  const { addressId } = useParams();
  const { updateToast } = useToastStore();
  const [currentAddress, setCurrentAddress] =
    useState<IMasterAddressPopulated>();
  const theme = useTheme();
  useEffect(() => {
    if (addressId) fetchAddressData();
  }, [addressId]);

  const fetchAddressData = async () => {
    try {
      const res = await MasterIndexApi.getMasterAddress(addressId || '');
      setCurrentAddress(res.data);
      if (res.data.uniqueId && setCustomTitle) {
        setCustomTitle(
          `${generateUniqueId('ADR', res.data.uniqueId)} - ${
            res.data.intersectionStreets.length
              ? getNormalizedAddressText(res.data)
              : getAddressLabelText(res.data)
          }`,
        );
      }
      setLastUpdatedAt?.(res.data?.updatedAt);
    } catch (err: any) {
      updateToast({
        open: true,
        message: err.message || 'Failed to fetch address details',
      });
    }
  };

  const getValue = (item: IViewItem): string => {
    if (!currentAddress) return '';
    const itemValue = currentAddress[item.value as keyof IMasterAddress];

    // these fields don't have values for intersections by design
    if (currentAddress.intersectionStreets?.length) {
      if (
        ['addressLabel', 'addressNumber', 'completeStreetName'].includes(
          item.value as string,
        )
      ) {
        return 'N/A';
      }
    }

    if (
      ['createdAt', 'updatedAt'].includes(item.value as string) &&
      itemValue
    ) {
      return moment(itemValue as string).format(DATE_FORMAT);
    }

    if (item.value === 'point' && itemValue) {
      const point = itemValue as IMasterPoint;
      return `${point.coordinates[1]}, ${point.coordinates[0]}`;
    }

    if (item.value === 'intersectionStreets') {
      return Array.isArray(itemValue) ? (itemValue as string[]).join(', ') : '';
    }

    if (item.value === 'nearestIntersection') {
      return Array.isArray(itemValue) ? (itemValue as string[]).join(', ') : '';
    }

    if (item.value === 'crossStreets') {
      let ret = 'N/A';

      if (currentAddress.crossStreet1 && currentAddress.distance1) {
        ret = `${currentAddress?.crossStreet1}(${formatDistance(
          currentAddress?.distance1,
        )})`;
      }

      if (currentAddress.crossStreet2 && currentAddress.distance2) {
        ret += ` and ${currentAddress?.crossStreet2}(${formatDistance(
          currentAddress?.distance2,
        )})`;
      }

      return ret;
    }

    if (item.value === 'addressLabel') {
      return getAddressLabelText(currentAddress) || 'N/A';
    }

    if (item.value === 'normalized') {
      return getNormalizedAddressText(currentAddress) || 'N/A';
    }

    if (itemValue === null) {
      return 'N/A';
    }

    if (typeof itemValue === 'object') {
      return JSON.stringify(itemValue);
    }

    return String(itemValue);
  };

  const getPreplanValue = (prePlanItem: any) => {
    const addressPrePlan = getAddressPrePlan(currentAddress);
    const rawValue = addressPrePlan?.[prePlanItem.name.split('.').pop()] || '';
    switch (prePlanItem.type) {
      case 'select': {
        if (prePlanItem.items === 'getAgencyEmployees') {
          const submittedByUser = rawValue as {
            firstName?: string;
            lastName?: string;
          } | null;
          return formatPersonName(submittedByUser) || 'N/A';
        }
        return [...prePlanItem.items].find((item) => item.value === rawValue)
          ?.label;
      }

      case 'date': {
        return rawValue ? moment(rawValue).format(DATE_FORMAT) : '';
      }
      default:
        return rawValue;
    }
  };
  const renderViewCardContent = ({
    items,
    size,
  }: (typeof MasterAddressViewFields)[0]) => {
    return items.map((item) => {
      const shouldShowMapsIcon =
        item.value === 'addressLabel' || item.value === 'normalized';

      return (
        <ViewItem
          key={item.label}
          label={item.label}
          value={getValue(item)}
          md={4}
          {...size}
          copyable
          showMapsIcon={shouldShowMapsIcon}
        />
      );
    });
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflow: 'auto',
          p: 2.5,
          background: theme.palette.background.paper,
          borderRadius: 2,
        }}
      >
        <Stack spacing={2}>
          {currentAddress &&
            MasterAddressViewFields.map((data, index) => (
              <ViewCard
                key={data.key}
                title={data.title}
                showDivider={index !== MasterAddressViewFields.length - 1}
              >
                {renderViewCardContent(data)}
              </ViewCard>
            ))}

          {currentAddress &&
            currentAgency &&
            !!getAddressPrePlan(currentAddress) &&
            PrePlanMdf &&
            PrePlanMdf[currentAgency?.addressPrePlanType] && (
              <ViewCard title="Pre-plan checklist">
                {PrePlanMdf[currentAgency.addressPrePlanType]?.map(
                  (prePlanItem: any) => {
                    return prePlanItem.type === 'file' ? (
                      <FileGrid files={getPreplanValue(prePlanItem)} />
                    ) : (
                      <ViewItem
                        key={prePlanItem.name}
                        label={prePlanItem.label}
                        value={getPreplanValue(prePlanItem)}
                        md={4}
                      ></ViewItem>
                    );
                  },
                )}
              </ViewCard>
            )}
        </Stack>
      </Box>
    </Box>
  );
};
